import { z } from 'zod'

/** RFC 7807 problem details as returned by the API (see DomainExceptionHandler on the backend). */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  code?: string
  traceId?: string
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails | null,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Stable machine-readable code from the backend (e.g. "invalid-lineup"), when present. */
  get code(): string | undefined {
    return this.problem?.code
  }
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  schema?: z.ZodType<T>,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  if (UNSAFE_METHODS.has(method)) {
    // Same-origin cookie auth requires a CSRF token on unsafe methods.
    const xsrf = readCookie('hff_csrf')
    if (xsrf) {
      headers['X-XSRF-TOKEN'] = xsrf
    }
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    let problem: ProblemDetails | null = null
    try {
      problem = (await response.json()) as ProblemDetails
    } catch {
      problem = null
    }
    throw new ApiError(response.status, problem, problem?.detail ?? problem?.title ?? response.statusText)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const data = (await response.json()) as unknown
  return schema ? schema.parse(data) : (data as T)
}

/** Thin same-origin JSON client. Feature API modules build on this (see src/api/*.api.ts). */
export const http = {
  get: <T>(path: string, schema?: z.ZodType<T>) => request<T>('GET', path, undefined, schema),
  post: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) => request<T>('POST', path, body, schema),
  put: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) => request<T>('PUT', path, body, schema),
  patch: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) =>
    request<T>('PATCH', path, body, schema),
  del: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) =>
    request<T>('DELETE', path, body, schema),
}
